import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import {
  Inbox, UserCheck, Target, CheckCircle, MoreVertical, Mail,
  Phone, Building2, Clock, ExternalLink, MessageSquare, Filter
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_CONFIG = {
  new: { label: "New", color: "bg-blue-100 text-blue-700 border-blue-200", icon: Inbox },
  contacted: { label: "Contacted", color: "bg-yellow-100 text-yellow-700 border-yellow-200", icon: Mail },
  qualified: { label: "Qualified", color: "bg-purple-100 text-purple-700 border-purple-200", icon: Target },
  converted: { label: "Converted", color: "bg-green-100 text-green-700 border-green-200", icon: CheckCircle }
};

const SOURCE_LABELS = {
  website_contact_form: "Contact Page",
  storefront_contact: "Storefront",
  booking: "Booking",
  other: "Other"
};

export const Leads = ({ onRefresh }) => {
  const [leads, setLeads] = useState([]);
  const [activeFilter, setActiveFilter] = useState("all");

  const fetchLeads = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/leads`);
      setLeads(res.data);
    } catch (error) {
      console.error("Error fetching leads:", error);
    }
  }, []);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const handleStatusUpdate = async (id, newStatus) => {
    try {
      await axios.put(`${API}/leads/${id}/status`, { status: newStatus });
      fetchLeads();
    } catch (error) {
      console.error("Error updating lead status:", error);
    }
  };

  const filteredLeads = activeFilter === "all" ? leads : leads.filter(l => l.status === activeFilter);

  const counts = {
    all: leads.length,
    new: leads.filter(l => l.status === "new").length,
    contacted: leads.filter(l => l.status === "contacted").length,
    qualified: leads.filter(l => l.status === "qualified").length,
    converted: leads.filter(l => l.status === "converted").length
  };

  const conversionRate = leads.length > 0
    ? Math.round((counts.converted / leads.length) * 100) : 0;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="leads-view">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-cabinet text-3xl font-bold text-slate-900">Lead Management</h2>
          <p className="text-slate-500 font-manrope">Track and convert website inquiries</p>
        </div>
        <Button variant="outline" onClick={fetchLeads} data-testid="refresh-leads-btn">
          <Filter size={16} className="mr-2" /> Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { key: "all", label: "Total Leads", value: counts.all, icon: MessageSquare, iconColor: "text-slate-600", bgColor: "bg-slate-100" },
          { key: "new", label: "New", value: counts.new, icon: Inbox, iconColor: "text-blue-600", bgColor: "bg-blue-100" },
          { key: "contacted", label: "Contacted", value: counts.contacted, icon: Mail, iconColor: "text-yellow-600", bgColor: "bg-yellow-100" },
          { key: "qualified", label: "Qualified", value: counts.qualified, icon: Target, iconColor: "text-purple-600", bgColor: "bg-purple-100" },
          { key: "converted", label: "Converted", value: counts.converted, icon: CheckCircle, iconColor: "text-green-600", bgColor: "bg-green-100" }
        ].map(stat => {
          const Icon = stat.icon;
          return (
            <Card
              key={stat.key}
              className={`cursor-pointer transition-all duration-200 ${activeFilter === stat.key ? 'ring-2 ring-royal-blue-500 shadow-lg' : 'hover:shadow-md'}`}
              onClick={() => setActiveFilter(stat.key)}
              data-testid={`lead-stat-${stat.key}`}
            >
              <CardContent className="pt-5 pb-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${stat.bgColor}`}>
                    <Icon size={18} className={stat.iconColor} />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">{stat.label}</p>
                    <p className="font-cabinet text-xl font-bold">{stat.value}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Conversion Rate Banner */}
      {leads.length > 0 && (
        <Card className="border-green-200 bg-gradient-to-r from-green-50 to-emerald-50">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-100">
                  <Target size={18} className="text-green-700" />
                </div>
                <div>
                  <p className="text-sm font-medium text-green-800">Lead Conversion Rate</p>
                  <p className="text-xs text-green-600">{counts.converted} converted out of {leads.length} total leads</p>
                </div>
              </div>
              <span className="font-cabinet text-3xl font-bold text-green-700">{conversionRate}%</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Leads Table */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">
              {activeFilter === "all" ? "All Leads" : `${STATUS_CONFIG[activeFilter]?.label || activeFilter} Leads`}
              <span className="text-sm font-normal text-slate-500 ml-2">({filteredLeads.length})</span>
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-t">
              <tr>
                <th className="text-left p-4 font-medium text-sm">Contact</th>
                <th className="text-left p-4 font-medium text-sm">Details</th>
                <th className="text-left p-4 font-medium text-sm">Source</th>
                <th className="text-left p-4 font-medium text-sm">Subject / Message</th>
                <th className="text-left p-4 font-medium text-sm">Status</th>
                <th className="text-left p-4 font-medium text-sm">Received</th>
                <th className="text-right p-4 font-medium text-sm">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredLeads.map(lead => {
                const statusConf = STATUS_CONFIG[lead.status] || STATUS_CONFIG.new;
                return (
                  <tr key={lead.id} className="border-b hover:bg-slate-50 transition-colors" data-testid={`lead-row-${lead.id}`}>
                    <td className="p-4">
                      <p className="font-medium text-slate-900">{lead.name}</p>
                      <div className="flex items-center gap-1 text-xs text-slate-500 mt-0.5">
                        <Mail size={11} /> {lead.email}
                      </div>
                    </td>
                    <td className="p-4">
                      {lead.phone && (
                        <div className="flex items-center gap-1 text-sm text-slate-600">
                          <Phone size={12} /> {lead.phone}
                        </div>
                      )}
                      {lead.company && (
                        <div className="flex items-center gap-1 text-sm text-slate-600">
                          <Building2 size={12} /> {lead.company}
                        </div>
                      )}
                    </td>
                    <td className="p-4">
                      <Badge variant="outline" className="text-xs">
                        {SOURCE_LABELS[lead.source] || lead.source}
                      </Badge>
                    </td>
                    <td className="p-4 max-w-xs">
                      {lead.subject && <p className="text-sm font-medium text-slate-700 truncate">{lead.subject}</p>}
                      <p className="text-sm text-slate-500 truncate">{lead.message}</p>
                    </td>
                    <td className="p-4">
                      <Badge className={statusConf.color}>{statusConf.label}</Badge>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-1 text-sm text-slate-500">
                        <Clock size={12} />
                        {new Date(lead.created_at).toLocaleDateString()}
                      </div>
                      <p className="text-xs text-slate-400">{new Date(lead.created_at).toLocaleTimeString()}</p>
                    </td>
                    <td className="p-4 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" data-testid={`lead-actions-${lead.id}`}>
                            <MoreVertical size={16} />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleStatusUpdate(lead.id, 'new')}>
                            <Inbox size={14} className="mr-2 text-blue-600" /> Mark as New
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleStatusUpdate(lead.id, 'contacted')}>
                            <Mail size={14} className="mr-2 text-yellow-600" /> Mark as Contacted
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleStatusUpdate(lead.id, 'qualified')}>
                            <Target size={14} className="mr-2 text-purple-600" /> Mark as Qualified
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleStatusUpdate(lead.id, 'converted')}>
                            <CheckCircle size={14} className="mr-2 text-green-600" /> Mark as Converted
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filteredLeads.length === 0 && (
            <div className="text-center py-12">
              <Inbox size={40} className="mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500 font-medium">
                {activeFilter === "all" ? "No leads yet" : `No ${STATUS_CONFIG[activeFilter]?.label.toLowerCase()} leads`}
              </p>
              <p className="text-sm text-slate-400 mt-1">
                Leads from the contact form and storefront will appear here
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
